import { useQuery } from '@tanstack/react-query';

import { priceService } from '@services/priceService';
import { getCurrentTime } from '@services/timeshiftService';
import type { Asset } from '@shared/types';

export interface MinuteCandle {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface UseDayCandlesResult {
  candles: MinuteCandle[];
  isLoading: boolean;
  error: string | null;
  lastUpdate: number | null;
}

// Fetch a full day of 1-minute candles in a single call
const fetchDayCandles = async (asset: Asset): Promise<MinuteCandle[]> => {
  const now = getCurrentTime();
  const startOfDay = new Date(now - 24 * 60 * 60 * 1000); // 24 hours ago
  const endOfDay = new Date(now);

  const candleData = await priceService.fetchCandles(
    asset,
    '1m', // 1 minute intervals
    1440, // 24 hours * 60 minutes = 1440 candles max
    startOfDay.toISOString(),
    endOfDay.toISOString(),
  );

  return candleData.map(candle => ({
    timestamp: new Date(candle.openTime).getTime(),
    open: parseFloat(candle.open),
    high: parseFloat(candle.high),
    low: parseFloat(candle.low),
    close: parseFloat(candle.close),
    volume: 0, // Your API doesn't return volume, set to 0
  }));
};

/**
 * Clear cached candles for an asset
 */
export const clearIncompleteCandles = (
  asset: Asset,
  queryClient: any,
): void => {
  queryClient.removeQueries({ queryKey: ['dayCandles', asset] });
  console.log(`[Candles] Cleared cached candles for ${asset}`);
};

/**
 * Hook to fetch a full day (24 hours) of 1-minute candles for an asset
 * Simple single API call approach
 */
export function useDayCandles(asset: Asset): UseDayCandlesResult {
  const {
    data: candles = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['dayCandles', asset],
    queryFn: () => fetchDayCandles(asset),
    staleTime: 60 * 1000, // Refetch every minute for latest data
    gcTime: 5 * 60 * 1000, // Keep in memory for 5 minutes
    retry: 2,
  });

  return {
    candles,
    isLoading,
    error: error?.message ?? null,
    lastUpdate: isLoading ? null : getCurrentTime(),
  };
}
